/**
 * Role helpers. Mirrors the backend hierarchy in abol_hr_backend/src/utils/roles.ts.
 *
 * Roles are hierarchical: super-admin > admin > user.
 *
 * `super-admin` is a platform level role, but inside an organization it must behave
 * exactly like `admin` — same navigation, same pages, same actions. Comparing with
 * `role === EmployeeRoles.admin` silently fails for a super admin and drops them into
 * the plain employee UI, so every *permission gate* asks `isAdminRole()` instead.
 *
 * Sites where the role is a stored value rather than a permission question (the role
 * picker on an employee, knowledge base `allowedRoles`, directory filters) keep using
 * the `EmployeeRoles` members directly — widening those would corrupt data.
 */
import { EmployeeRoles } from '@/utils/enums';

type Role = string | null | undefined;

const normalize = (role: Role): string => String(role ?? '');

export const SUPER_ADMIN_ROLES: string[] = [EmployeeRoles.superAdmin];
export const ADMIN_ROLES: string[] = [EmployeeRoles.superAdmin, EmployeeRoles.admin];
export const USER_ROLES: string[] = [EmployeeRoles.user];

/** True when the role may do everything an organization admin may do. The permission gate. */
export const isAdminRole = (role: Role): boolean => ADMIN_ROLES.includes(normalize(role));

/** True for the platform level role only. */
export const isSuperAdminRole = (role: Role): boolean => SUPER_ADMIN_ROLES.includes(normalize(role));

/**
 * The roles a tenant admin may hand out from the employee settings page. `super-admin`
 * is granted by the platform (backend `npm run create:super-admin`) and is deliberately
 * not offered here, so an organization cannot mint one for itself.
 */
export const ASSIGNABLE_ROLES: EmployeeRoles[] = [EmployeeRoles.user, EmployeeRoles.admin];

/** True when `viewer` is allowed to change `target`'s role. Nobody may change a super admin's. */
export const canAssignRole = (viewerRole: Role, targetRole: Role): boolean =>
  isAdminRole(viewerRole) && !isSuperAdminRole(targetRole);

const ROLE_LABELS: Record<string, string> = {
  [EmployeeRoles.superAdmin]: 'Super Admin',
  [EmployeeRoles.admin]: 'Admin',
  [EmployeeRoles.user]: 'Employee',
};

/** Display label for a role badge. Unknown roles read as `Employee`. */
export const roleLabel = (role: Role): string => ROLE_LABELS[normalize(role)] ?? 'Employee';
